import React, { useEffect, useState } from "react";
import Loader from "../../components/loader/Loader";
import { getAllOrders, updateOrderStatus } from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import { toast } from "react-toastify";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./style.css";

const statusClass = (status) =>
  `order-badge order-badge-${(status || "").toLowerCase()}`;

// The set the backend's updateOrder() validates against; anything else is
// rejected as "Invalid order status".
const ORDER_STATUSES = ["Processing", "Ordered", "Delivered", "Cancelled"];

function AllOrders() {
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  // Order id currently being saved, so only that row shows a pending state.
  const [savingId, setSavingId] = useState(null);
  // The order open in the update dialog, and the status chosen inside it.
  const [updateTarget, setUpdateTarget] = useState(null);
  const [draftStatus, setDraftStatus] = useState("");

  const fetchAllOrders = async () => {
    setIsLoading(true);
    try {
      const response = await getAllOrders();
      if (response.success) {
        setOrders(response.orders || []);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.log(error);
      toast.error("Something Went wrong!!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllOrders();
  }, []);

  const toggleRow = (orderId) =>
    setExpandedId((prev) => (prev === orderId ? null : orderId));

  const openUpdate = (order) => {
    setUpdateTarget(order);
    setDraftStatus(order.orderStatus || "");
  };

  const closeUpdate = () => {
    if (savingId) return; // don't drop the dialog mid-save
    setUpdateTarget(null);
    setDraftStatus("");
  };

  const submitStatusUpdate = async () => {
    const order = updateTarget;
    if (!order || !draftStatus || draftStatus === order.orderStatus) return;

    setSavingId(order._id);
    try {
      const response = await updateOrderStatus(order._id, draftStatus);
      if (response?.success) {
        // Trust the server's copy rather than assuming the write applied.
        // userId and orderItems are populated on the list query but not on the
        // update response, so keep the richer local versions.
        setOrders((prev) =>
          prev.map((item) =>
            item._id === order._id
              ? {
                  ...item,
                  ...response.order,
                  userId: item.userId,
                  orderItems: item.orderItems,
                }
              : item
          )
        );
        toast.success(`Order marked as ${draftStatus}`);
        setUpdateTarget(null);
        setDraftStatus("");
      } else {
        toast.error(response?.message || "Could not update the order status.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Could not update the order status.");
    } finally {
      setSavingId(null);
    }
  };

  const term = search.trim().toLowerCase();
  const filteredOrders = !term
    ? orders
    : orders.filter((order) => {
        const haystack = [
          order._id,
          order.orderStatus,
          order.paymentInfo?.mode,
          order.paymentInfo?.status,
          order.paymentInfo?.ref,
          order.userId?.name,
          order.userId?.email,
          order.userId?.phone,
          order.shippingInfo?.city,
          order.shippingInfo?.state,
          order.shippingInfo?.pincode,
          order.shippingInfo?.phoneNo,
          ...(order.orderItems || []).map((item) => item.productId?.title),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });

  const itemCount = (order) =>
    (order.orderItems || []).reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="investment-history">
          <h1 className="main-heading">All Orders</h1>
          <br />
          <br />

          <div className="orders-table-wrap">
            <div className="orders-table-top">
              <h2>All Orders details</h2>
              <input
                type="text"
                className="orders-search"
                placeholder="Search order id, customer, product, status..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            {filteredOrders.length === 0 ? (
              <p className="orders-empty">No data available</p>
            ) : (
              <div className="orders-table-scroll">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>S.no</th>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total Amount</th>
                      <th>Payment</th>
                      <th>Order Status</th>
                      <th>Ordered On</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, index) => {
                      const isOpen = expandedId === order._id;
                      return (
                        <React.Fragment key={order._id}>
                          <tr
                            className={`orders-row ${isOpen ? "is-open" : ""}`}
                            onClick={() => toggleRow(order._id)}
                          >
                            <td className="orders-toggle">
                              {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </td>
                            <td>{index + 1}</td>
                            <td className="orders-id">{order._id}</td>
                            <td>
                              <span className="orders-strong">
                                {order.userId?.name || "N/A"}
                              </span>
                              <span className="orders-sub">
                                {order.userId?.email || "-"}
                              </span>
                            </td>
                            <td>{itemCount(order)}</td>
                            <td>Rs {order.totalAmount}</td>
                            <td>
                              <span className={statusClass(order.paymentInfo?.status)}>
                                {order.paymentInfo?.status || "-"}
                              </span>
                              <span className="orders-sub">
                                {order.paymentInfo?.mode || "-"}
                              </span>
                            </td>
                            <td>
                              <span className={statusClass(order.orderStatus)}>
                                {order.orderStatus}
                              </span>
                            </td>
                            <td>{order.createdAt ? formatDate(order.createdAt) : "-"}</td>
                            <td onClick={(event) => event.stopPropagation()}>
                              <button
                                type="button"
                                className="orders-update-btn"
                                onClick={() => openUpdate(order)}
                              >
                                Update
                              </button>
                            </td>
                          </tr>

                          {isOpen && (
                            <tr className="orders-detail-row">
                              <td colSpan={10}>
                                <div className="orders-detail">
                                  <div className="orders-detail-grid">
                                    <div className="orders-card">
                                      <h3>Customer</h3>
                                      <p><span>Name</span>{order.userId?.name || "N/A"}</p>
                                      <p><span>Email</span>{order.userId?.email || "N/A"}</p>
                                      <p><span>Phone</span>{order.userId?.phone || "N/A"}</p>
                                      <p><span>Wallet</span>{order.userId?.userWalletAddress || "N/A"}</p>
                                      <p><span>User ID</span>{order.userId?._id || "N/A"}</p>
                                    </div>

                                    <div className="orders-card">
                                      <h3>Shipping Address</h3>
                                      <p><span>Address</span>{order.shippingInfo?.address || "N/A"}</p>
                                      <p><span>City</span>{order.shippingInfo?.city || "N/A"}</p>
                                      <p><span>State</span>{order.shippingInfo?.state || "N/A"}</p>
                                      <p><span>Country</span>{order.shippingInfo?.country || "N/A"}</p>
                                      <p><span>Pincode</span>{order.shippingInfo?.pincode || "N/A"}</p>
                                      <p><span>Phone</span>{order.shippingInfo?.phoneNo || "N/A"}</p>
                                    </div>

                                    <div className="orders-card">
                                      <h3>Payment &amp; Delivery</h3>
                                      <p><span>Mode</span>{order.paymentInfo?.mode || "N/A"}</p>
                                      <p><span>Status</span>{order.paymentInfo?.status || "N/A"}</p>
                                      <p><span>Reference</span>{order.paymentInfo?.ref || "N/A"}</p>
                                      <p><span>Total</span>Rs {order.totalAmount}</p>
                                      <p>
                                        <span>Estimated</span>
                                        {order.estimatedDelivery
                                          ? formatDate(order.estimatedDelivery)
                                          : "N/A"}
                                      </p>
                                      <p>
                                        <span>Shipped At</span>
                                        {order.shippedAt ? formatDate(order.shippedAt) : "N/A"}
                                      </p>
                                    </div>
                                  </div>

                                  {order.options && order.options.length > 0 && (
                                    <div className="orders-card orders-card-full">
                                      <h3>Selected Options</h3>
                                      {order.options.map((option, i) => (
                                        <p key={i}>
                                          <span>{option.optionName}</span>
                                          {(option.optionValue || []).join(", ")}
                                        </p>
                                      ))}
                                    </div>
                                  )}

                                  <h3 className="orders-items-heading">
                                    Products in this order
                                  </h3>
                                  <div className="orders-items-scroll">
                                    <table className="orders-items-table">
                                      <thead>
                                        <tr>
                                          <th>Image</th>
                                          <th>Product Name</th>
                                          <th>Vendor</th>
                                          <th>Category</th>
                                          <th>Variant</th>
                                          <th>Barcode</th>
                                          <th>Qty</th>
                                          <th>Selling Price</th>
                                          <th>Subtotal</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(order.orderItems || []).map((item, i) => {
                                          const product = item.productId;
                                          const image =
                                            product?.imageSrc && product.imageSrc[0];
                                          return (
                                            <tr key={item._id || i}>
                                              <td>
                                                {image ? (
                                                  <img
                                                    className="orders-item-img"
                                                    src={image}
                                                    alt={
                                                      (product?.imageAltText &&
                                                        product.imageAltText[0]) ||
                                                      product?.title ||
                                                      "product"
                                                    }
                                                  />
                                                ) : (
                                                  <div className="orders-item-img orders-item-noimg">
                                                    N/A
                                                  </div>
                                                )}
                                              </td>
                                              <td>
                                                <span className="orders-strong">
                                                  {product?.title || "Product unavailable"}
                                                </span>
                                                <span className="orders-sub">
                                                  {product?._id || item.productId || "-"}
                                                </span>
                                              </td>
                                              <td>{product?.vendor || "-"}</td>
                                              <td>
                                                {(product?.productCategory || []).join(", ") ||
                                                  "-"}
                                              </td>
                                              <td>
                                                {(product?.variant || []).join(", ") || "-"}
                                              </td>
                                              <td>{product?.variantBarcode || "-"}</td>
                                              <td>{item.quantity}</td>
                                              <td>Rs {item.sellingPrice}</td>
                                              <td>Rs {item.sellingPrice * item.quantity}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {updateTarget && (
        <div
          className="orders-modal-backdrop"
          onClick={closeUpdate}
          role="presentation"
        >
          <div
            className="orders-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="orders-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="orders-modal-head">
              <h3 id="orders-modal-title">Update Order Status</h3>
              <button
                type="button"
                className="orders-modal-close"
                onClick={closeUpdate}
                disabled={!!savingId}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="orders-modal-body">
              <p className="orders-modal-meta">
                <span>Order ID</span>
                {updateTarget._id}
              </p>
              <p className="orders-modal-meta">
                <span>Customer</span>
                {updateTarget.userId?.name || "N/A"}
                {updateTarget.userId?.email ? ` (${updateTarget.userId.email})` : ""}
              </p>
              <p className="orders-modal-meta">
                <span>Total</span>Rs {updateTarget.totalAmount}
              </p>
              <p className="orders-modal-meta">
                <span>Current</span>
                <span className={statusClass(updateTarget.orderStatus)}>
                  {updateTarget.orderStatus}
                </span>
              </p>

              <label className="orders-modal-label" htmlFor="orders-modal-status">
                New status
              </label>
              <select
                id="orders-modal-status"
                className="orders-modal-select"
                value={draftStatus}
                disabled={!!savingId}
                onChange={(event) => setDraftStatus(event.target.value)}
              >
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>

              {draftStatus === "Cancelled" &&
                updateTarget.orderStatus !== "Cancelled" && (
                  <p className="orders-modal-warn">
                    The customer will see this order as cancelled. Stock is not
                    returned to inventory automatically.
                  </p>
                )}
            </div>

            <div className="orders-modal-foot">
              <button
                type="button"
                className="orders-modal-btn is-ghost"
                onClick={closeUpdate}
                disabled={!!savingId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="orders-modal-btn is-primary"
                onClick={submitStatusUpdate}
                disabled={!!savingId || draftStatus === updateTarget.orderStatus}
              >
                {savingId ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AllOrders;
